/**
 * Per-unit cost breakdown card (CHO-752): rolled up by top-level category
 * prefix, expandable to raw-category detail, with period share, trend vs the
 * previous period of equal length, and the excluded-row count. Prop-driven,
 * no fetching — the breakdown comes from lib/dashboard-costs.ts.
 */
import { useState } from 'react';
import type { BreakdownRow, UnitBreakdown } from '../../lib/dashboard-costs';
import { formatCurrency } from '../../lib/format';

export interface CostBreakdownCardProps {
  propertyName: string;
  currency: string;
  breakdown: UnitBreakdown;
  excludedCount: number;
}

function TrendCell({ trend }: { trend: number | null }) {
  if (trend == null) {
    return <span className="text-sm text-gray-300">—</span>;
  }
  const positive = trend > 0;
  const flat = trend === 0;
  return (
    <span
      className={`text-sm font-medium ${
        flat ? 'text-gray-400' : positive ? 'text-emerald-600' : 'text-rose-600'
      }`}
    >
      {flat ? '→' : positive ? '▲' : '▼'} {Math.abs(trend)}%
    </span>
  );
}

function ShareBar({ share }: { share: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-gray-100 rounded overflow-hidden">
        <div className="h-full bg-gray-400 rounded" style={{ width: `${Math.min(100, share * 100)}%` }} />
      </div>
      <span className="text-xs text-gray-500">{Math.round(share * 100)}%</span>
    </div>
  );
}

function PrefixRow({ row, currency }: { row: BreakdownRow; currency: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-50 text-left"
      >
        <span className={`text-xs text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}>
          ▸
        </span>
        <span className="flex-1 text-sm font-medium text-gray-800">{row.prefix}</span>
        <span className="hidden sm:block w-24">
          <ShareBar share={row.share} />
        </span>
        <span className="w-16 text-right">
          <TrendCell trend={row.trend} />
        </span>
        <span className="w-28 text-right text-sm font-semibold text-gray-900">
          {formatCurrency(row.total, currency)}
        </span>
      </button>
      {expanded && (
        <div className="ml-8 border-l border-gray-100 pl-3 my-1 space-y-1">
          {row.detail.map((d) => (
            <div key={d.category} className="flex items-center gap-3 py-1">
              <span className="flex-1 text-xs font-mono text-gray-500">{d.category}</span>
              <span className="hidden sm:block w-24">
                <ShareBar share={d.share} />
              </span>
              <span className="w-16 text-right">
                <TrendCell trend={d.trend} />
              </span>
              <span className="w-28 text-right text-xs text-gray-700">
                {formatCurrency(d.total, currency)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CostBreakdownCard({
  propertyName,
  currency,
  breakdown,
  excludedCount,
}: CostBreakdownCardProps) {
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{propertyName}</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {excludedCount} excluded {excludedCount === 1 ? 'row' : 'rows'} omitted
          </p>
        </div>
        <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded px-2 py-1">
          {currency}
        </span>
      </div>
      <div className="flex items-baseline gap-3 mb-4">
        <span className="text-2xl font-bold text-gray-900">
          {formatCurrency(breakdown.total, currency)}
        </span>
        {breakdown.prevTotal != null && (
          <span className="text-xs text-gray-400">
            prev {formatCurrency(breakdown.prevTotal, currency)}
          </span>
        )}
      </div>
      {breakdown.rows.length === 0 ? (
        <p className="text-sm text-gray-400">No costs in this period.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {breakdown.rows.map((row) => (
            <PrefixRow key={row.prefix} row={row} currency={currency} />
          ))}
        </div>
      )}
    </div>
  );
}
